import { __spreadArray } from "tslib";
var PasswordStrengthExtractor = /** @class */ (function () {
    function PasswordStrengthExtractor() {
        var _this = this;
        this.getLengthScore = function (password) {
            var _a, _b, _c;
            var hints = [];
            if (_this.policy) {
                var minLength = (_b = (_a = _this.policy) === null || _a === void 0 ? void 0 : _a.minLength) !== null && _b !== void 0 ? _b : PasswordStrengthExtractor.MINIMAL_LENGTH;
                if (minLength <= (password === null || password === void 0 ? void 0 : password.length)) {
                    return { lengthScore: 5, lengthHints: [] };
                }
                hints.push('more_characters');
                return { lengthScore: 0, lengthHints: hints };
            }
            if ((password === null || password === void 0 ? void 0 : password.length) < PasswordStrengthExtractor.MINIMAL_LENGTH) {
                hints.push('too_short');
            }
            var length = Math.max((_c = password === null || password === void 0 ? void 0 : password.length) !== null && _c !== void 0 ? _c : 0, PasswordStrengthExtractor.MINIMAL_LENGTH);
            if (length < 16) {
                hints.push('more_characters');
            }
            var getLengthScore = function () {
                switch (length) {
                    case 6:
                        return -4;
                    case 7:
                    case 8:
                        return -2;
                    case 9:
                    case 10:
                        return 0;
                    case 11:
                    case 12:
                    case 13:
                        return 2;
                    case 14:
                    case 15:
                        return 3;
                    default:
                        // 16 and above
                        return 5;
                }
            };
            return { lengthScore: getLengthScore(), lengthHints: hints };
        };
        this.getComplexityScore = function (password) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            var minNumOfUpperCase = (_b = (_a = _this.policy) === null || _a === void 0 ? void 0 : _a.minUpperCase) !== null && _b !== void 0 ? _b : 1;
            var minNumOfLowerCase = (_d = (_c = _this.policy) === null || _c === void 0 ? void 0 : _c.minLowerCase) !== null && _d !== void 0 ? _d : 1;
            var minNumOfNumbers = (_f = (_e = _this.policy) === null || _e === void 0 ? void 0 : _e.minNumber) !== null && _f !== void 0 ? _f : 1;
            var minNumOfSpecialChars = (_h = (_g = _this.policy) === null || _g === void 0 ? void 0 : _g.minSymbol) !== null && _h !== void 0 ? _h : 1;
            var allowedRepetitiveCharsLength = (_k = (_j = _this.policy) === null || _j === void 0 ? void 0 : _j.allowedRepetitiveCharsLength) !== null && _k !== void 0 ? _k : 2;
            var commonChecks = [
                function (password) {
                    if (password === void 0) { password = ''; }
                    var lowerCaseLetters = password.match(/[a-z]/g);
                    return !lowerCaseLetters || (lowerCaseLetters === null || lowerCaseLetters === void 0 ? void 0 : lowerCaseLetters.length) < minNumOfLowerCase
                        ? 'letters'
                        : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var upperCaseLetters = password.match(/[A-Z]/g);
                    return !upperCaseLetters || upperCaseLetters.length < minNumOfUpperCase
                        ? 'letters'
                        : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var numbers = password.match(/[0-9]/g);
                    return !numbers || numbers.length < minNumOfNumbers
                        ? 'numbers'
                        : undefined;
                },
            ];
            var checksWithoutPolicy = __spreadArray(__spreadArray([], commonChecks, true), [
                function (password) {
                    if (password === void 0) { password = ''; }
                    var specialChars = password.match(/[!-/:-@[-`{-~]/g);
                    return !specialChars || specialChars.length < minNumOfSpecialChars
                        ? 'symbol'
                        : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var isRepetitive = _this.hasRepetitiveChars(password, allowedRepetitiveCharsLength);
                    return isRepetitive ? 'repeating_characters' : undefined;
                },
            ], false);
            var checksWithPolicy = __spreadArray(__spreadArray([], commonChecks, true), [
                function (password) {
                    if (password === void 0) { password = ''; }
                    var specialChars = password.match(/[!-/:-@[-`{-~]/g);
                    return !specialChars || specialChars.length < minNumOfSpecialChars
                        ? 'symbol'
                        : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var isRepetitive = _this.hasRepetitiveChars(password, allowedRepetitiveCharsLength);
                    return isRepetitive ? 'repeating_characters' : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var hasForbiddenChars = /[^ !-/:-@[-`{-~a-zA-Z0-9]/g.test(password);
                    return hasForbiddenChars ? 'forbidden_characters' : undefined;
                },
                function (password) {
                    if (password === void 0) { password = ''; }
                    var hasBlankSpaces = password.trim().length !== password.length;
                    return hasBlankSpaces ? 'blank_spaces' : undefined;
                },
            ], false);
            var checks = _this.policy ? checksWithPolicy : checksWithoutPolicy;
            var hints = checks.map(function (test) { return test(password); }).filter(function (a) { return a; });
            var complexityScore = checks.length - hints.length;
            // Correct score in case of no policy and no repeating characters
            if (!_this.policy && !hints.includes('repeating_characters')) {
                complexityScore--;
            }
            return {
                complexityScore: complexityScore,
                hints: hints,
            };
        };
        this.hasRepetitiveChars = function (password, maxRepeatingChars) {
            var _a;
            var repeatingCharExpression = new RegExp("(.)\\1{".concat(maxRepeatingChars, ",}"), 'g');
            var repeatingCharsCount = 0;
            var repeatingChars = repeatingCharExpression.exec(password);
            while (repeatingChars) {
                var match = repeatingChars[0];
                repeatingCharsCount = Math.max(match.length, repeatingCharsCount);
                repeatingChars = repeatingCharExpression.exec(password);
            }
            var isRepetitive = ((_a = _this.policy) === null || _a === void 0 ? void 0 : _a.allowedRepetitiveCharsLength)
                ? repeatingCharsCount > maxRepeatingChars
                : repeatingCharsCount / (password === null || password === void 0 ? void 0 : password.length) > 0.5;
            return isRepetitive;
        };
        this.getScoreLabel = function (score) {
            score = Math.max(score, 0);
            switch (score) {
                case 0:
                case 1:
                    return '1'; // weak
                case 2:
                    return '2'; // average
                case 3:
                case 4:
                    return '3'; // good
                default:
                    // 5 and above - strong
                    return '4';
            }
        };
    }
    PasswordStrengthExtractor.getInstance = function () {
        if (PasswordStrengthExtractor.instance) {
            return PasswordStrengthExtractor.instance;
        }
        return new PasswordStrengthExtractor();
    };
    PasswordStrengthExtractor.prototype.withPolicy = function (policy) {
        this.policy = policy;
        return this;
    };
    PasswordStrengthExtractor.prototype.getPasswordStrength = function (password) {
        var _a = this.getComplexityScore(password), complexityScore = _a.complexityScore, complexityHints = _a.hints;
        var _b = this.getLengthScore(password), lengthScore = _b.lengthScore, lengthHints = _b.lengthHints;
        var finalScore = complexityScore + lengthScore;
        if (this.policy) {
            var isPasswordContainsForbiddenChars = /[^!-/:-@[-`{-~a-zA-Z0-9]/g.test(password) &&
                password.trim().length !== password.length;
            if (isPasswordContainsForbiddenChars) {
                complexityHints.push('forbidden_characters');
            }
            return {
                finalScore: finalScore,
                classification: this.getScoreLabel(finalScore),
                complexityScore: complexityScore,
                lengthScore: lengthScore,
                hints: lengthHints.concat(complexityHints),
            };
        }
        var repetitivityScore = complexityHints.includes('repeating_characters') && lengthScore < 3
            ? -1
            : 0;
        finalScore += repetitivityScore;
        return {
            finalScore: finalScore,
            repetitivityScore: repetitivityScore,
            classification: this.getScoreLabel(finalScore),
            complexityScore: complexityScore,
            lengthScore: lengthScore,
            hints: lengthHints.concat(complexityHints),
        };
    };
    PasswordStrengthExtractor.prototype.getPasswordStrengthForBiEvent = function (password) {
        var _a;
        var score = (_a = this.getPasswordStrength(password).finalScore) !== null && _a !== void 0 ? _a : 0;
        score = Math.max(score, 0);
        switch (score) {
            case 0:
            case 1:
                return 1;
            case 2:
            case 3:
                return 3;
            default:
                return 5;
        }
    };
    PasswordStrengthExtractor.MINIMAL_LENGTH = 6;
    PasswordStrengthExtractor.MAXIMAL_LENGTH = 100;
    return PasswordStrengthExtractor;
}());
export { PasswordStrengthExtractor };
//# sourceMappingURL=passwordStrength.js.map